import { Inject, Injectable } from '@angular/core';
import { Observable, map, zip } from 'rxjs';

import { triggerAmplitudeEventFromAvo, triggerBrazeEventFromAvo } from '@amplitude/helpers/gtm-functions';
import { environment as ENV } from '@environment';
import { 
  AppEnvironment, 
  AppEnvironmentValueType, 
  AvoEnv, 
  CustomDestination, 
  SourceValueType,
  initAvo 
} from '@avo/Avo';
import {
  AvoBaseProperties, AvoProductProps, AvoSearchProps, AvoStoreConfig, AvoUserAddress, AvoUserData
} from '@avo/interface/avo-entities';
import { EventProperties, ProductProperties, SearchProperties } from '@avo/interface/trigger-props';
import { isDevelopmentEnvironmentByAmplitude } from '@amplitude/mappers/is-development-amplitude.mapper';
import { isNullOrUndefined } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { getTriggerEvent, getEventProps } from '@avo/mappers/trigger-event.mapper';
import { TriggerEvent } from '@avo/interface/trigger-entity';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';

const ANONYMOUS = 'Anónimo';

@Injectable()
export class AvoService {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected Inspector: any;
  protected initAvoDispatch = initAvo;

  constructor(
    private readonly appFacade: AppFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    this.Inspector = this.isBrowser ? require('avo-inspector') : null;
  }

  public isProductionEnvironment = ENV.production;

  get logEventAvailable(): boolean {
    return this.isProductionEnvironment || isDevelopmentEnvironmentByAmplitude()
  }

  get env(): AvoEnv {
    return this.isProductionEnvironment ? AvoEnv.Prod : AvoEnv.Dev;
  }

  get appEnvironment(): AppEnvironmentValueType {
    return this.isProductionEnvironment ? AppEnvironment.PRODUCTION : AppEnvironment.DEVELOPMENT
  }

  public initAvo(): void {
    if (this.Inspector) {
      const inspector = new this.Inspector.AvoInspector({
        apiKey: ENV.avoKey,
        env: this.Inspector.AvoInspectorEnv.Dev,
        version: ENV.version,
        appName: 'ng-rappi-travel'
      });
      this.initAvoDispatch(
        { env: this.env, inspector },
        {
          appVersion: ENV.version,
          appEnvironment: this.appEnvironment,
          storePlatform: null,
        },
        {},
        this.customAmplitudeSetup,
        this.customFirebaseSetup,
        this.customBrazeEventSetup
      );
    }
  }

  public customAmplitudeSetup: CustomDestination = {
    logEvent: (eventName: string, eventProperties: EventProperties) => {
      if (this.logEventAvailable) {
        const { 
          USER_ID, LOYALTY_LEVEL, IS_PRIME, SELECTED_ADDRESS, HAS_RAPPICARD, USER_FULLNAME, REWARD_LEVEL,
          LAT, LNG, MICROZONE_NAME, MICROZONE_ID,
          ...properties 
        } = eventProperties; 
        triggerAmplitudeEventFromAvo({
          event: 'triggerAmplitudeEvent',
          eventType: eventName,
          eventProperties: properties,
          userProperties: {
            USER_ID: USER_ID as string,
            IS_PRIME: IS_PRIME as boolean,
            ADDRESS: SELECTED_ADDRESS as string, 
            HAS_RAPPICARD: HAS_RAPPICARD as boolean,
            USER_FULLNAME: USER_FULLNAME as string,
            REWARD_LEVEL: REWARD_LEVEL as string,
            LAT: LAT as string,
            LNG: LNG as string,
            MICROZONE_NAME: MICROZONE_NAME as string,
            MICROZONE_ID: MICROZONE_ID as number
          }
        });
      }
    },
  };

  public customFirebaseSetup: CustomDestination = {
    logEvent: (eventName: string, eventProperties: EventProperties) => {
      if (this.logEventAvailable) {
      }
    },
  };

  public customBrazeEventSetup: CustomDestination = {
    logEvent: (eventName: string, eventProperties: EventProperties) => {
      triggerBrazeEventFromAvo(eventName, eventProperties);
    },
  };

  public avoBaseProperties(trigger: TriggerEvent): AvoBaseProperties {
    return {
      ...this.avoUserAddress(trigger),
      ...this.avoUserData(trigger),
      ...this.avoStoreData(),
    };
  }
  
  public getAvoSourceValueType(type: unknown): SourceValueType {
    return type as SourceValueType;
  }

  public avoProductProps(props: ProductProperties): AvoProductProps {
    return {
      travelProductCode: props?.TRAVEL_PRODUCT_CODE,
      travelProductName: props?.TRAVEL_PRODUCT_NAME,
      travelProductPrice: props?.TRAVEL_PRODUCT_PRICE ?? null,
      travelTotalPrice: props?.TRAVEL_TOTAL_PRICE ?? null,
      travelProductProvider: props?.TRAVEL_PRODUCT_PROVIDER,
      travelSubProduct: props?.TRAVEL_SUB_PRODUCT ?? null,
      travelProductCategory: props?.TRAVEL_PRODUCT_CATEGORY ?? null,
      travelHasMemberRate: !isNullOrUndefined(props?.TRAVEL_HAS_MEMBER_RATE) ? props?.TRAVEL_HAS_MEMBER_RATE : null,
    };
  }

  public avoSearchProps(props: SearchProperties): AvoSearchProps {
    return {
      travelProductType: props?.TRAVEL_PRODUCT_TYPE,
      travelFrom: props?.TRAVEL_FROM,
      travelTo: props?.TRAVEL_TO,
      travelDestinationType: props?.TRAVEL_DESTINATION_TYPE ?? null,
      travelTripType: props?.TRAVEL_TRIP_TYPE ?? null,
      travelAdvancedPurchase: props?.TRAVEL_ADVANCED_PURCHASE as string,
      travelDateFrom: props?.TRAVEL_DATE_FROM as string,
      travelDateTo: props?.TRAVEL_DATE_TO as string,
      travelTripLength: !isNullOrUndefined(props?.TRAVEL_TRIP_LENGTH) ? props?.TRAVEL_TRIP_LENGTH as number : null,
      travelPassengers: !isNullOrUndefined(props?.TRAVEL_PASSENGERS) ? props?.TRAVEL_PASSENGERS as number : null,
      travelAdults: !isNullOrUndefined(props?.TRAVEL_ADULTS) ? props?.TRAVEL_ADULTS as number : null,
      travelChildren: !isNullOrUndefined(props?.TRAVEL_CHILDREN) ? props?.TRAVEL_CHILDREN as number : null,
      travelInfants: !isNullOrUndefined(props?.TRAVEL_INFANTS) ? props?.TRAVEL_INFANTS as number : null,
      travelClassOfService: props?.TRAVEL_CLASS_OF_SERVICE ?? null,
      travelRooms: !isNullOrUndefined(props?.TRAVEL_ROOMS) ? props?.TRAVEL_ROOMS as number : null,
      travelWithBaggage: !isNullOrUndefined(props?.TRAVEL_WITH_BAGGAGE) ? props?.TRAVEL_WITH_BAGGAGE : null,
      travelBranchCode: props?.TRAVEL_BRANCH_CODE,
      travelSearchId: props?.TRAVEL_SEARCH_ID ?? null,
      travelHotelDestinationId: props?.TRAVEL_HOTEL_DESTINATION_ID ?? null,
      travelHotelDestinationType: props?.TRAVEL_HOTEL_DESTINATION_TYPE ?? null,
    };
  }

  public getEventTriggerProps$(
    eventName?: string,
    eventParams?: EventProperties,
    searchProps?: SearchProperties,
    productProps?: ProductProperties
  ): Observable<TriggerEvent> {
    return zip(
      this.appFacade.user$,
      this.appFacade.trackingUtm$,
      this.appFacade.userAddresses$,
      this.mobileDeviceService.isMobileDevice$
    ).pipe(
      map(([user, utms, userAddresses, isMobile]) => {
        return getTriggerEvent(
          eventName,
          getEventProps(isMobile, utms, eventParams),
          searchProps,
          productProps,
          userAddresses,
          user
        );
      })
    );
  }

  private avoUserData(trigger: TriggerEvent): AvoUserData {
    return {
      isPrime: trigger.USER?.is_prime ?? false,
      hasRappicard: trigger.USER?.has_rappicard ?? false,
      userId: trigger.USER?.id?.toString(),
      userFullname: trigger.USER?.name,
      loyaltyLevel: trigger.USER?.loyalty?.name,
      travelPlatform: trigger.EVENT_PROPS?.TRAVEL_PLATFORM as string
    };
  }

  private avoUserAddress(trigger: TriggerEvent): AvoUserAddress {
    const activeAddress = trigger?.USER_ADDRESSES?.activeAddress ?? undefined;
    return {
      address: activeAddress ? activeAddress.address : ANONYMOUS,
      addressId: activeAddress ? activeAddress.id : 0,
      addressDescription: activeAddress ? activeAddress.description : ANONYMOUS,
      addressTag: activeAddress ? activeAddress.tag : ANONYMOUS,
      city: activeAddress?.city?.city ?? ANONYMOUS,
      country: ENV.countryInfo.iso_code.toUpperCase(),
      microzoneName: activeAddress?.zone?.name ?? ANONYMOUS,
      microzoneId: activeAddress?.zone?.id?.toString() ?? '0',
      lat: +activeAddress?.lat,
      lng: +activeAddress?.lng,
    };
  }

  private avoStoreData(): AvoStoreConfig {
    const { SUPPORT_LINK, ...store_config } = ENV.store_configuration;  
    return {
      storeType: store_config.STORE_TYPE,
      storeId: store_config.STORE_ID.toString(),
      storeName: store_config.STORE_NAME,
      verticalGroup: store_config.VERTICAL_GROUP,
      verticalSubGroup: store_config.VERTICAL_SUB_GROUP
    };
  }

}
